/**
 * BESTIX v5.0 — VIP Package Card
 * Premium experience packages
 */

import { Link } from 'react-router-dom';
import { Crown, Utensils, Car, Gift, Star, Check } from 'lucide-react';
import { cn, formatPrice } from '@/lib/utils';

export interface VIPPackageProps {
  id: string;
  name: string;
  description: string;
  price: number;
  originalPrice?: number;
  image: string;
  includes: string[];
  tier: 'gold' | 'platinum' | 'diamond';
  eventId?: string;
}

const tierConfig = {
  gold: {
    color: 'from-amber-500 to-amber-600',
    badge: 'bg-amber-500',
    icon: Star,
  },
  platinum: {
    color: 'from-purple-500 to-purple-600',
    badge: 'bg-purple-500',
    icon: Crown,
  },
  diamond: {
    color: 'from-cyan-400 to-blue-500',
    badge: 'bg-gradient-to-r from-cyan-400 to-blue-500',
    icon: Crown,
  },
};

export function VIPPackageCard({
  id,
  name,
  description,
  price,
  originalPrice,
  image,
  includes,
  tier,
  eventId,
}: VIPPackageProps) {
  const config = tierConfig[tier];
  const Icon = config.icon;
  const discount = originalPrice ? Math.round((1 - price / originalPrice) * 100) : 0;

  return (
    <div className="group bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300">
      {/* Image */}
      <div className="relative aspect-[16/9]">
        <img src={image} alt={name} className="w-full h-full object-cover" />
        <div className={cn('absolute inset-0 bg-gradient-to-t opacity-60', config.color)} />
        
        {/* Badge */}
        <div className={cn('absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 rounded-full text-white text-sm font-bold', config.badge)}>
          <Icon className="w-4 h-4" />
          {tier.toUpperCase()}
        </div>

        {/* Discount */}
        {discount > 0 && (
          <div className="absolute top-4 right-4 px-3 py-1 bg-[var(--color-coral)] text-white rounded-full text-sm font-bold">
            {discount}% OFF
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-6">
        <h3 className="font-playfair text-xl text-[var(--color-navy)] mb-2">{name}</h3>
        <p className="text-gray-600 text-sm mb-4 line-clamp-2">{description}</p>

        {/* Includes */}
        <div className="space-y-2 mb-6">
          {includes.slice(0, 4).map((item, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
              <span className="text-gray-600">{item}</span>
            </div>
          ))}
          {includes.length > 4 && (
            <p className="text-sm text-[var(--color-gold)]">+{includes.length - 4} more included</p>
          )}
        </div>

        {/* Price */}
        <div className="flex items-end justify-between mb-4">
          <div>
            {originalPrice && (
              <p className="text-sm text-gray-400 line-through">{formatPrice(originalPrice)}</p>
            )}
            <p className="text-2xl font-bold text-[var(--color-navy)]">{formatPrice(price)}</p>
            <p className="text-xs text-gray-500">per person</p>
          </div>
        </div>

        {/* CTA */}
        <Link
          to={eventId ? `/events/${eventId}?vip=${id}` : `/vip/${id}`}
          className="block w-full py-3 text-center bg-[var(--color-navy)] text-white rounded-full font-medium hover:bg-[var(--color-navy-light)] transition-colors selection:bg-white/40 selection:text-white group-hover:bg-[var(--color-gold)] group-hover:text-[var(--color-navy)] group-hover:selection:bg-[var(--color-navy)]/30 group-hover:selection:text-[var(--color-navy)]"
        >
          View Package
        </Link>
      </div>
    </div>
  );
}

export default VIPPackageCard;
